import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Building2, User, KeyRound, ArrowRight, ArrowLeft, Mail, Lock, Phone } from 'lucide-react';
import api from '../services/api';

export default function AuthModal({ isOpen, onClose, defaultRole = 'candidate' }) {
  const [role, setRole] = useState(defaultRole === 'recruiter' ? 'employer' : defaultRole); 
  const [loginMethod, setLoginMethod] = useState('otp');
  
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState(''); 
  const [otp, setOtp] = useState('');
  const [sessionId, setSessionId] = useState('');
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    setRole(defaultRole === 'recruiter' ? 'employer' : defaultRole); 
    setStep(1);
    setLoginMethod('otp');
    setPhone('');
    setOtp('');
    setEmail('');
    setPassword('');
    setMessage({ type: '', text: '' });
  }, [isOpen, defaultRole]);

  if (!isOpen) return null;

  // 1️⃣ SEND OTP FUNCTION (FIXED PAYLOAD)
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (phone.length !== 10) {
      return setMessage({ type: 'error', text: 'Please enter a valid 10-digit mobile number.' });
    }

    setLoading(true);
    try {
      const endpoint = role === 'candidate' ? "/candidate/send-otp" : "/employer/send-otp";
      
      // 🌟 FIX: Candidate ke liye 'phone', Employer ke liye 'identifier'
      const payload = role === 'candidate' ? { phone: phone } : { identifier: phone };

      const res = await api.post(endpoint, payload); 
      
      if (res.data.success) {
        setSessionId(res.data.session_id);
        setStep(2);
        setMessage({ type: 'success', text: 'OTP sent successfully!' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to send OTP. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // 2️⃣ VERIFY OTP FUNCTION (FIXED PAYLOAD)
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (otp.length < 4) {
      return setMessage({ type: 'error', text: 'Please enter a valid OTP.' });
    }

    setLoading(true);
    try {
      const endpoint = role === 'candidate' ? "/candidate/verify-otp" : "/employer/verify-otp";
      
      // 🌟 FIX: Candidate ke liye 'phone', Employer ke liye 'identifier'
      const payload = role === 'candidate' 
        ? { phone: phone, session_id: sessionId, otp: otp }
        : { identifier: phone, session_id: sessionId, otp: otp };

      const res = await api.post(endpoint, payload);

      if (res.data.success) {
        saveTokenAndRedirect(res.data);
      } 
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Invalid OTP entered.' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    setLoading(true);
    try {
      const res = await api.post('/employer/login', { email, password });
      
      if (res.data.status === 'success') {
        saveTokenAndRedirect(res.data);
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Invalid email or password.' });
    } finally {
      setLoading(false);
    }
  };

  const saveTokenAndRedirect = (data) => {
    const token = data.token || data.access_token;
    if (token) {
      localStorage.setItem(role === 'candidate' ? "candidateToken" : "employer_token", token);
    }

    let extractedData = data.candidate || data.employer || data.user || data.data;
    let userData = { ...extractedData };
    
    if (userData) {
        delete userData.token;
        delete userData.access_token;
    }

    localStorage.setItem(role === 'candidate' ? "candidate" : "employer", JSON.stringify(userData || {}));

    setMessage({ type: "success", text: "Login Successful!" });

    setTimeout(() => {
      onClose();
      window.location.href = role === 'candidate' ? "/candidate-profile" : "/employer/profile";
    }, 1000);
  };

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    setStep(1);
    setLoginMethod('otp');
    setPhone('');
    setOtp('');
    setEmail('');
    setPassword('');
    setMessage({ type: '', text: '' });
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-md p-4 animate-in fade-in duration-200">
      <div className="relative flex flex-col md:flex-row w-full max-w-4xl bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800 scale-100 animate-in zoom-in-95 duration-200">
        
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 z-20 p-2 bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-black dark:hover:text-white rounded-full transition-all hover:rotate-90"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Left Side Branding */}
        <div className="hidden md:flex flex-col items-center justify-center w-5/12 bg-gradient-to-br from-slate-50 to-yellow-50/50 dark:from-gray-800 dark:to-gray-900 p-8 border-r border-gray-100 dark:border-gray-800 text-center relative overflow-hidden">
          <div className="absolute -top-10 -left-10 w-40 h-40 bg-yellow-400/20 rounded-full blur-2xl"></div>
          <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-orange-400/10 rounded-full blur-2xl"></div>
          
          <div className="relative z-10">
            <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white mb-2 tracking-tight">
              Let's get started
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-[13px] mb-8 leading-relaxed max-w-xs">
              {role === 'employer' 
                ? 'Hire top talent faster, manage candidates, and oversee your job listings effortlessly.' 
                : 'Connect with top-tier companies and land your dream job instantly.'}
            </p>
            <div className="w-44 h-44 mx-auto mb-6 bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 flex items-center justify-center">
               <img src="https://img.freepik.com/free-vector/telecommuting-concept-illustration_114360-1600.jpg" alt="Auth" className="w-full h-full object-contain mix-blend-multiply dark:mix-blend-normal" />
            </div>
          </div>
        </div>

        {/* Right Side Form */}
        <div className="w-full md:w-7/12 p-6 sm:p-10 max-h-[90vh] overflow-y-auto">
          
          {/* Main Heading */}
          <div className="mb-6 flex items-center gap-3">
             <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center text-yellow-600 dark:text-yellow-400">
               {role === 'candidate' ? <User size={20} /> : <Building2 size={20} />}
             </div>
             <div>
                <h3 className="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight">
                  {role === 'candidate' ? 'Candidate Login' : 'Employer Login'}
                </h3>
                <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">
                  {step === 1 ? 'Please select your login method below.' : `We've sent a code to +91 ${phone}`}
                </p>
             </div>
          </div>

          {/* 🌟 2-TAB ROLE SELECTOR */}
          {step === 1 && (
            <div className="flex bg-gray-100 dark:bg-gray-800 p-1.5 rounded-2xl mb-8 border border-gray-200 dark:border-gray-700/50">
                <button 
                  type="button" 
                  onClick={() => handleRoleChange('candidate')} 
                  className={`flex-1 text-[13px] font-bold py-2.5 rounded-xl transition-all duration-300 ${role === 'candidate' ? 'bg-white dark:bg-gray-700 shadow-sm text-black dark:text-white scale-[1.02]' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                >
                  Candidate
                </button>
                <button 
                  type="button" 
                  onClick={() => handleRoleChange('employer')} 
                  className={`flex-1 text-[13px] font-bold py-2.5 rounded-xl transition-all duration-300 ${role === 'employer' ? 'bg-white dark:bg-gray-700 shadow-sm text-black dark:text-white scale-[1.02]' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                >
                  Employer
                </button>
            </div>
          )}

          {message.text && (
            <div className={`mb-6 p-3.5 rounded-xl text-xs font-semibold flex items-center gap-2 animate-in fade-in ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-950/40 dark:border-green-800 dark:text-green-400' 
                : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/40 dark:border-red-800 dark:text-red-400'
            }`}>
              {message.text}
            </div>
          )}
          
          {step === 1 ? (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
              
              {/* 🌟 INTERACTIVE CARDS FOR RECRUITER LOGIN OPTIONS */}
              {role === 'employer' && (
                 <div className="grid grid-cols-2 gap-4 mb-8">
                    <button 
                      type="button"
                      onClick={() => setLoginMethod('otp')}
                      className={`relative flex flex-col items-center justify-center gap-3 p-5 rounded-2xl border-2 transition-all duration-300 ease-out group ${
                        loginMethod === 'otp' 
                          ? 'border-yellow-400 bg-yellow-50/40 dark:bg-yellow-900/10 text-gray-900 dark:text-white shadow-[0_0_15px_rgba(250,204,21,0.15)] scale-[1.02]' 
                          : 'border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-500 hover:border-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <div className={`p-3.5 rounded-full transition-colors duration-300 ${loginMethod === 'otp' ? 'bg-yellow-400 text-black shadow-inner' : 'bg-gray-100 dark:bg-gray-800 text-gray-400 group-hover:text-gray-600'}`}>
                        <Phone size={22} />
                      </div>
                      <span className="text-[13px] font-extrabold tracking-wide">Mobile OTP</span>
                      
                      {loginMethod === 'otp' && (
                        <div className="absolute -top-2.5 -right-2.5 bg-yellow-400 text-black text-[9px] font-extrabold px-2.5 py-1 rounded-full shadow-sm uppercase tracking-wider animate-in zoom-in">
                            Active
                        </div>
                      )}
                    </button>

                    <button 
                      type="button"
                      onClick={() => setLoginMethod('password')}
                      className={`relative flex flex-col items-center justify-center gap-3 p-5 rounded-2xl border-2 transition-all duration-300 ease-out group ${
                        loginMethod === 'password' 
                          ? 'border-yellow-400 bg-yellow-50/40 dark:bg-yellow-900/10 text-gray-900 dark:text-white shadow-[0_0_15px_rgba(250,204,21,0.15)] scale-[1.02]' 
                          : 'border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-500 hover:border-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <div className={`p-3.5 rounded-full transition-colors duration-300 ${loginMethod === 'password' ? 'bg-yellow-400 text-black shadow-inner' : 'bg-gray-100 dark:bg-gray-800 text-gray-400 group-hover:text-gray-600'}`}>
                        <Mail size={22} />
                      </div>
                      <span className="text-[13px] font-extrabold tracking-wide">Email Login</span>
                      
                      {loginMethod === 'password' && (
                        <div className="absolute -top-2.5 -right-2.5 bg-yellow-400 text-black text-[9px] font-extrabold px-2.5 py-1 rounded-full shadow-sm uppercase tracking-wider animate-in zoom-in">
                            Active
                        </div>
                      )}
                    </button>
                 </div>
              )}

              {/* 📱 LOGIN OPTIONS */}
              {loginMethod === 'otp' ? (
                // OPTION A: MOBILE OTP FORM
                <form onSubmit={handleSendOtp} className="space-y-6 animate-in fade-in zoom-in-95 duration-200">
                  <div>
                    <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-2">Mobile number</label>
                    <div className="flex relative border border-gray-300 dark:border-gray-700 rounded-xl overflow-hidden focus-within:border-yellow-400 focus-within:ring-1 focus-within:ring-yellow-400 transition-all bg-gray-50 dark:bg-gray-800 shadow-sm">
                      <div className="flex items-center justify-center bg-gray-100 dark:bg-gray-700 border-r border-gray-200 dark:border-gray-600 px-4 text-[14px] font-bold text-gray-600 dark:text-gray-300">
                        +91
                      </div>
                      <input 
                        type="tel" 
                        maxLength="10"
                        value={phone} 
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))} 
                        placeholder="Enter your 10-digit number" 
                        className="w-full px-4 py-3.5 text-[14px] bg-transparent outline-none dark:text-white font-semibold placeholder:text-gray-400" 
                        required 
                      />
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    disabled={loading || phone.length !== 10} 
                    className="w-full bg-gradient-to-r from-[#FED547] to-[#fcd33f] hover:from-[#fcd33f] hover:to-[#fbc72b] text-black font-extrabold py-3.5 rounded-xl transition-all shadow-lg shadow-yellow-400/30 text-[14px] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex justify-center items-center gap-2"
                  >
                    {loading ? 'Sending Code...' : 'Continue with OTP'} 
                    {!loading && <ArrowRight className="w-4 h-4" />}
                  </button>
                </form>
              ) : (
                // OPTION B: EMAIL & PASSWORD FORM (For Employer Only)
                <form onSubmit={handlePasswordLogin} className="space-y-5 animate-in fade-in zoom-in-95 duration-200">
                  <div>
                    <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-2">Email Address</label>
                    <div className="flex relative border border-gray-300 dark:border-gray-700 rounded-xl overflow-hidden focus-within:border-yellow-400 focus-within:ring-1 focus-within:ring-yellow-400 transition-all bg-gray-50 dark:bg-gray-800 shadow-sm">
                      <div className="flex items-center justify-center bg-gray-100 dark:bg-gray-700 border-r border-gray-200 dark:border-gray-600 px-4">
                        <Mail size={16} className="text-gray-500" />
                      </div>
                      <input 
                        type="email" 
                        value={email} 
                        onChange={(e) => setEmail(e.target.value)} 
                        placeholder="Enter your registered email" 
                        className="w-full px-4 py-3.5 text-[14px] bg-transparent outline-none dark:text-white font-semibold placeholder:text-gray-400" 
                        required 
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                        <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300">Password</label>
                    </div>
                    <div className="flex relative border border-gray-300 dark:border-gray-700 rounded-xl overflow-hidden focus-within:border-yellow-400 focus-within:ring-1 focus-within:ring-yellow-400 transition-all bg-gray-50 dark:bg-gray-800 shadow-sm">
                      <div className="flex items-center justify-center bg-gray-100 dark:bg-gray-700 border-r border-gray-200 dark:border-gray-600 px-4">
                        <Lock size={16} className="text-gray-500" />
                      </div>
                      <input 
                        type="password" 
                        value={password} 
                        onChange={(e) => setPassword(e.target.value)} 
                        placeholder="Enter your password" 
                        className="w-full px-4 py-3.5 text-[14px] bg-transparent outline-none dark:text-white font-semibold placeholder:text-gray-400" 
                        required 
                      />
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    disabled={loading || !email || !password} 
                    className="w-full bg-gradient-to-r from-[#FED547] to-[#fcd33f] hover:from-[#fcd33f] hover:to-[#fbc72b] text-black font-extrabold py-3.5 rounded-xl transition-all shadow-lg shadow-yellow-400/30 text-[14px] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex justify-center items-center gap-2 mt-4"
                  >
                    {loading ? 'Logging in...' : 'Login Securely'} 
                    {!loading && <ArrowRight className="w-4 h-4" />}
                  </button>
                </form>
              )}
            </div>
          ) : (
            // 🔐 STEP 2: OTP VERIFICATION FORM
            <form onSubmit={handleVerifyOtp} className="space-y-6 animate-in fade-in slide-in-from-right-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300">Enter OTP</label>
                  <button 
                    type="button" 
                    onClick={() => { setStep(1); setOtp(''); setMessage({type:'', text:''}); }}
                    className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-bold flex items-center gap-1 transition-colors"
                  >
                    <ArrowLeft size={12} /> Change Number
                  </button>
                </div>
                
                <div className="relative">
                  <input 
                    type="text" 
                    maxLength="6"
                    value={otp} 
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="• • • • • •" 
                    className="w-full border-2 border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3.5 text-center text-2xl tracking-[0.5em] font-extrabold text-gray-900 dark:text-white focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/50 bg-gray-50 dark:bg-gray-800 transition-all shadow-inner"
                    required 
                  />
                  <KeyRound className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 dark:text-gray-600 w-5 h-5" />
                </div>
              </div>

              <button 
                type="submit" 
                disabled={loading || otp.length < 4}
                className="w-full bg-gradient-to-r from-[#FED547] to-[#fcd33f] hover:from-[#fcd33f] hover:to-[#fbc72b] text-black font-extrabold py-3.5 rounded-xl transition-all shadow-lg shadow-yellow-400/30 text-[14px] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex justify-center items-center gap-2"
              >
                {loading ? 'Verifying...' : 'Verify & Login'}
              </button>
            </form>
          )}

          <div className="mt-8 text-center text-[12px] text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-800 pt-5">
            By clicking continue, you agree to the KadTech <a href="/terms-and-conditions" className="text-black font-semibold dark:text-white hover:text-yellow-500 transition-colors">Terms of service</a> & <a href="/privacy-policy" className="text-black font-semibold dark:text-white hover:text-yellow-500 transition-colors">Privacy policy</a>
          </div>
        </div>
        
      </div>
    </div>,
    document.body
  );
}